import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { AdminUser } from '@/hooks/admin/useAdminUsers';
import { useUserEntities } from '@/hooks/admin/useUserEntities';
import { usePurgeUserAccount } from '@/hooks/admin/usePurgeUserAccount';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface PurgeAccountModalProps {
    user: AdminUser;
    onClose: () => void;
}

export function PurgeAccountModal({ user, onClose }: PurgeAccountModalProps) {
    const [confirmed, setConfirmed] = useState(false);
    const { data, isLoading } = useUserEntities(user.id);
    const purgeAccount = usePurgeUserAccount();

    const totalEntities =
        (data?.collectionsCount ?? 0) +
        (data?.tagsCount ?? 0) +
        (data?.ledgersCount ?? 0);

    const handlePurge = async () => {
        if (!confirmed) {
            toast.error('Please confirm the purge action');
            return;
        }

        try {
            const result = await purgeAccount.mutateAsync(user.id);
            toast.success(result.message);
            onClose();
        } catch {
            toast.error('Failed to purge account. Please try again.');
        }
    };

    return (
        <Dialog open={true} onClose={onClose} title="Purge Account" size="md">
            {/* Warning header */}
            <div className="mb-4 flex items-start gap-3 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600 dark:text-red-400" />
                <div>
                    <p className="font-medium text-red-800 dark:text-red-300">
                        This action cannot be undone
                    </p>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                        This will permanently delete all data for this user while
                        keeping the account active.
                    </p>
                </div>
            </div>

            {/* User info */}
            <div className="mb-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
                <p className="text-sm text-gray-600 dark:text-gray-300">User:</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                    {user.email}
                </p>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                </div>
            ) : (
                <>
                    {/* Entity summary */}
                    <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-600">
                        <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                            Entities to be deleted:
                        </h4>
                        <ul className="space-y-1 text-sm">
                            <li className="text-gray-700 dark:text-gray-300">
                                Collections:{' '}
                                <span className="font-medium">
                                    {data?.collectionsCount ?? 0}
                                </span>
                            </li>
                            <li className="text-gray-700 dark:text-gray-300">
                                Tags:{' '}
                                <span className="font-medium">
                                    {data?.tagsCount ?? 0}
                                </span>
                            </li>
                            <li className="text-gray-700 dark:text-gray-300">
                                Ledgers:{' '}
                                <span className="font-medium">
                                    {data?.ledgersCount ?? 0}
                                </span>
                                <span className="text-gray-500">
                                    {' '}
                                    (including all their values and returns)
                                </span>
                            </li>
                        </ul>
                        <p className="mt-2 font-semibold text-gray-900 dark:text-white">
                            Total: {totalEntities} entities
                        </p>
                    </div>

                    {totalEntities === 0 ? (
                        <p className="mb-4 text-center text-gray-500 dark:text-gray-400">
                            No entities to purge for this user.
                        </p>
                    ) : (
                        /* Confirmation checkbox */
                        <div className="mb-4">
                            <label className="flex cursor-pointer items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={confirmed}
                                    onChange={(e) =>
                                        setConfirmed(e.target.checked)
                                    }
                                    className="h-4 w-4 rounded-sm border-gray-300 text-red-600 focus:ring-red-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    I understand this action is irreversible
                                </span>
                            </label>
                        </div>
                    )}
                </>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={handlePurge}
                    isLoading={purgeAccount.isPending}
                    disabled={!confirmed || totalEntities === 0 || isLoading}
                >
                    Purge Account
                </Button>
            </div>
        </Dialog>
    );
}
